// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { useCallback, useEffect, useState } from "react";
import debounce from "lodash/debounce";
import { xClassNames } from "@stoneage/XDL/Utilities";
import { SwiperInstance } from "@stoneage/App/Components/XngCarousel/models";

const NAVIGATION_EVENTS = ["slideChange", "reachBeginning", "reachEnd", "fromEdge", "slidesUpdated", "update"] as const;

interface ArrowElementProps {
    swiper: SwiperInstance | null;
    arrowType: string;
    showOnMobile?: boolean;
}

interface ArrowState {
    className: string;
    disabled: boolean;
}

const INITIAL_STATE: ArrowState = { className: "", disabled: true };

export function useArrowElement({ swiper, arrowType, showOnMobile }: ArrowElementProps): ArrowState {
    const [state, setState] = useState<ArrowState>(INITIAL_STATE);

    const updateState = useCallback(
        (instance: SwiperInstance) => {
            const nav = instance.navigation;
            if (!nav?.nextEl || !nav?.prevEl) return;

            const arrowEl = arrowType === "next" ? nav.nextEl : nav.prevEl;
            const isDisabled = arrowEl.classList.contains("swiper-button-disabled");
            const isLocked = arrowEl.classList.contains("swiper-button-lock");

            const className = xClassNames.append([
                `arrow-button xng-carousel-${arrowType}`,
                showOnMobile ? "arrow-on-mobile" : "arrow-on-desktop",
                isLocked ? "swiper-button-lock" : "",
                isDisabled ? "swiper-button-disabled" : "",
            ]);

            setState({ className, disabled: isDisabled });
        },
        [arrowType, showOnMobile],
    );

    useEffect(() => {
        if (!swiper) return;

        const debouncedUpdate = debounce(() => updateState(swiper), 100);

        NAVIGATION_EVENTS.forEach(event => swiper.on(event, debouncedUpdate));
        updateState(swiper);

        return () => {
            NAVIGATION_EVENTS.forEach(event => swiper.off(event, debouncedUpdate));
            debouncedUpdate.cancel();
        };
    }, [swiper, updateState]);

    return state;
}
